import React, { useState, useEffect } from 'react';
import {
  FiClock,
  FiCheckCircle,
  FiAlertCircle,
  FiXCircle,
  FiPauseCircle,
  FiPlayCircle,
  FiMoreVertical
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import ProgressBar from './ProgressBar';
import CountdownTimer from './CountdownTimer';

const PerformanceTaskCard = ({ task, showActions = false, onPause, onResume, onDelete }) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'paused':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'scheduled':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'completed':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'failed':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'ended':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTaskTypeLabel = (type) => {
    switch (type) {
      case 'sales_count':
        return 'Sales Count';
      case 'sales_amount':
        return 'Sales Revenue';
      case 'tiles_quantity':
        return 'Tiles Sold';
      case 'accessories_quantity':
        return 'Accessories Sold';
      default:
        return type;
    }
  };

  const getUnitLabel = (type) => {
    switch (type) {
      case 'sales_count':
        return 'sales';
      case 'sales_amount':
        return 'NGN';
      case 'tiles_quantity':
        return 'cartons';
      case 'accessories_quantity':
        return 'units';
      default:
        return '';
    }
  };

  const progress = parseFloat(task.progress_percentage || 0);
  const target = parseFloat(task.target_value);
  const current = parseFloat(task.current_progress || 0);

  return (
    <div
      className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 hover:shadow-md transition-shadow cursor-pointer relative"
      onClick={() => navigate(`/performance-tasks/${task.id}`)}
    >
      <div className="flex justify-between items-start mb-3">
        <div>
          <span
            className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(task.status)} mb-2 inline-block`}
          >
            {task.status.charAt(0).toUpperCase() + task.status.slice(1)}
          </span>
          <h3 className="font-semibold text-gray-900 dark:text-white text-lg line-clamp-1">
            {task.title}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">{task.task_number}</p>
        </div>
        {showActions && (
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-1 hover:bg-gray-100 rounded-full"
            >
              <FiMoreVertical />
            </button>
            {menuOpen && (
              <div className="absolute right-0 mt-2 w-32 bg-white rounded-md shadow-lg py-1 z-10 border border-gray-100">
                {onPause && task.status === 'active' && (
                  <button
                    onClick={() => {
                      onPause(task);
                      setMenuOpen(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Pause
                  </button>
                )}
                {onResume && task.status === 'paused' && (
                  <button
                    onClick={() => {
                      onResume(task);
                      setMenuOpen(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Resume
                  </button>
                )}
                <button
                  onClick={() => navigate(`/performance-tasks/${task.id}`)}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  View Details
                </button>
                {onDelete &&
                  (task.status === 'cancelled' ||
                    task.status === 'completed' ||
                    task.status === 'failed' ||
                    task.status === 'ended') && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(task);
                        setMenuOpen(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      Delete
                    </button>
                  )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mb-4">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-gray-600 dark:text-gray-400">
            {getTaskTypeLabel(task.task_type)}
          </span>
          <span className="font-medium text-gray-900 dark:text-white">
            {current.toLocaleString()} / {target.toLocaleString()} {getUnitLabel(task.task_type)}
          </span>
        </div>
        <ProgressBar current={current} target={target} size="md" />
      </div>

      <div className="flex items-center justify-between text-sm text-gray-500 border-t pt-3 dark:border-gray-700">
        <div className="flex items-center gap-1.5">
          <FiClock className={task.status === 'active' ? 'text-green-500' : 'text-gray-400'} />
          {task.status === 'active' ? (
            <span className="text-green-600 font-medium">
              <CountdownTimer endTime={task.extended_end_time || task.end_time} /> left
            </span>
          ) : task.status === 'scheduled' ? (
            <span>Starts: {new Date(task.start_time).toLocaleDateString()}</span>
          ) : (
            <span>Ended</span>
          )}
        </div>
        {task.assigned_count > 0 && (
          <span className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded text-xs">
            {task.assigned_count} assigned
          </span>
        )}
      </div>
    </div>
  );
};

export default PerformanceTaskCard;
